import React from "react";
import { USER_TYPES, getUserTypeMeta } from "../constants/userTypes";

const fieldLabelStyle = {
  display: "block",
  marginBottom: "8px",
  color: "#312e81",
  fontSize: "13px",
  fontWeight: 700,
  letterSpacing: "0.02em",
};

/**
 * Modern selectable cards for user type. Scales when USER_TYPES grows.
 */
export function UserTypePicker({ value, onChange, disabled = false, name = "user_type" }) {
  return (
    <fieldset
      style={{
        margin: 0,
        padding: 0,
        border: "none",
        minWidth: 0,
      }}
      disabled={disabled}
    >
      <legend style={{ ...fieldLabelStyle, padding: 0 }}>User type</legend>
      <div
        role="radiogroup"
        aria-label="User type"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "10px",
        }}
      >
        {USER_TYPES.map((type) => {
          const selected = value === type.value;
          return (
            <label
              key={type.value || "standard"}
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                padding: "12px 14px",
                borderRadius: "10px",
                border: selected ? `2px solid ${type.accent}` : "1px solid #e5e7eb",
                backgroundColor: selected ? type.softBg : "#ffffff",
                boxShadow: selected ? `0 0 0 3px ${type.softBorder}` : "none",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
                boxSizing: "border-box",
                minHeight: "72px",
              }}
            >
              <input
                type="radio"
                name={name}
                value={type.value}
                checked={selected}
                onChange={() => onChange(type.value)}
                disabled={disabled}
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: 0,
                  height: 0,
                  margin: 0,
                }}
              />
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: selected ? type.accent : "#1f2937",
                  }}
                >
                  {type.label}
                </span>
                <span
                  aria-hidden
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    border: selected ? `5px solid ${type.accent}` : "2px solid #d1d5db",
                    boxSizing: "border-box",
                    flexShrink: 0,
                    background: "#fff",
                  }}
                />
              </span>
              <span
                style={{
                  fontSize: "12px",
                  lineHeight: 1.4,
                  color: "#6b7280",
                  fontWeight: 500,
                }}
              >
                {type.description}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * Compact badge + native select for table rows.
 */
export function UserTypeSelect({ value, onChange, disabled = false, ariaLabel }) {
  const meta = getUserTypeMeta(value);

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          height: "22px",
          padding: "0 8px",
          borderRadius: "999px",
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.02em",
          color: meta.accent,
          backgroundColor: meta.softBg,
          border: `1px solid ${meta.softBorder}`,
          whiteSpace: "nowrap",
        }}
      >
        {meta.label}
      </span>
      <select
        value={value || ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        style={{
          height: "32px",
          padding: "0 28px 0 10px",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          backgroundColor: "#fff",
          color: "#111827",
          fontSize: "12px",
          fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
          minWidth: "118px",
          appearance: "none",
          WebkitAppearance: "none",
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M2.5 4.5L6 8l3.5-3.5'/%3E%3C/svg%3E\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 8px center",
          outline: "none",
        }}
      >
        {USER_TYPES.map((type) => (
          <option key={type.value || "standard"} value={type.value}>
            {type.label}
          </option>
        ))}
      </select>
    </div>
  );
}
